import { useMemo, useState } from "react";
import { useAllCustomerRepresentativesQuery } from "../../api/queries/customerOrganizationQueries";
import {
  defaultPaidArchiveParams,
  usePaidArchiveDashboardQuery,
  type PaidArchiveDashboardParams,
} from "../../api/queries/paymentsQueries";
import { displayCurrencyAmount } from "../../utils/display";
import { PaidInvoicesChart } from "./PaidInvoicesChart";
import { PaidInvoicesFilters } from "./PaidInvoicesFilters";
import { PaidInvoicesTable } from "./PaidInvoicesTable";

export function PaidInvoicesDashboard() {
  const [params, setParams] = useState<PaidArchiveDashboardParams>(() =>
    defaultPaidArchiveParams(),
  );
  const dashboardQuery = usePaidArchiveDashboardQuery(params);
  const customersQuery = useAllCustomerRepresentativesQuery();

  const customerOptions = useMemo(() => {
    const items = customersQuery.data?.items ?? [];
    return items
      .map((customer) => ({
        id: customer.id,
        // API `name` is primary contact name; company name is customer_organization_name.
        name:
          customer.customer_organization_name?.trim() ||
          customer.name?.trim() ||
          `Customer #${customer.id}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [customersQuery.data?.items]);

  const updateParams = (patch: Partial<PaidArchiveDashboardParams>) => {
    setParams((prev) => ({
      ...prev,
      ...patch,
      skip: patch.skip !== undefined ? patch.skip : 0,
    }));
  };

  const summary = dashboardQuery.data?.summary;
  const avgDays = summary?.avg_days_after_due;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Paid invoices</h2>
        <p className="text-sm text-gray-500">
          Settled invoices by customer and period
        </p>
      </div>

      <PaidInvoicesFilters
        customerId={params.customerId}
        customerOptions={customerOptions}
        statuses={params.statuses}
        dateField={params.dateField}
        dateFrom={params.dateFrom}
        dateTo={params.dateTo}
        onCustomerIdChange={(customerId) => updateParams({ customerId })}
        onStatusesChange={(statuses) => updateParams({ statuses })}
        onDateFieldChange={(dateField) => updateParams({ dateField })}
        onDateFromChange={(dateFrom) => updateParams({ dateFrom })}
        onDateToChange={(dateTo) => updateParams({ dateTo })}
        onClear={() => setParams(defaultPaidArchiveParams())}
      />

      {dashboardQuery.isError ? (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          Failed to load paid invoices dashboard.
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Invoices
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {dashboardQuery.isLoading ? "…" : (summary?.invoice_count ?? 0)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Total amount
          </p>
          <div className="mt-1 space-y-0.5">
            {dashboardQuery.isLoading ? (
              <p className="text-2xl font-semibold text-gray-900">…</p>
            ) : summary?.amount_by_currency.length ? (
              summary.amount_by_currency.map((row) => (
                <p
                  key={row.currency}
                  className="text-xl font-semibold tabular-nums text-gray-900"
                >
                  {displayCurrencyAmount(row.amount, row.currency)}
                </p>
              ))
            ) : (
              <p className="text-2xl font-semibold tabular-nums text-gray-900">
                —
              </p>
            )}
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Avg days after due
          </p>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
            {dashboardQuery.isLoading
              ? "…"
              : avgDays == null
                ? "—"
                : avgDays > 0
                  ? `${avgDays} late`
                  : avgDays < 0
                    ? `${Math.abs(avgDays)} early`
                    : "on time"}
          </p>
        </div>
      </div>

      <PaidInvoicesChart monthly={dashboardQuery.data?.monthly ?? []} />

      <PaidInvoicesTable
        items={dashboardQuery.data?.items ?? []}
        total={dashboardQuery.data?.total ?? 0}
        skip={params.skip ?? 0}
        limit={params.limit ?? 50}
        onPageChange={(skip) =>
          setParams((prev) => ({
            ...prev,
            skip,
          }))
        }
      />
    </div>
  );
}
